import axios from "./../axios/axios";
import { AnyAction, ThunkAction } from "@reduxjs/toolkit";
import { RootState } from "./index";
import { authActions } from "./auth-slice";
import { baseUrl } from "../axios/axios";
import { validateToken, getTokenAuthorities } from "../utils/tokenUtils";
import {
  derivePersonaKey,
  derivePharmacistPersona,
} from "../utils/pharmacistPersona";

/**
 * ITAM-2622: which login branch a role set reaches. This is the single
 * classification `login()` and `loginCheckState()` both switch on — the
 * precedence lives HERE and nowhere else, while each caller keeps its own
 * ending (a fresh attempt must preserve `loginError`, a restore must not
 * proceed to background work, a refresh must discard a token it did not
 * rewrite).
 *
 * The pharmacist branch sits after every genuine staff branch — and after the
 * retail branch for the dual-role session — so those match first and win
 * untouched (decision 6). The one branch it outranks is DRIVER: the backend
 * grants DELIVERY_USER to ordinary CUSTOMER accounts when their delivery order
 * is completed (CnCOrderServiceImpl), so that role is not reliable staff
 * evidence and must not suppress a valid pharmacist session. A delivery-only
 * token still falls through to DRIVER.
 */
export type SessionPersonaKind =
  | "ADMIN"
  | "MARKETPLACE_ADMIN"
  | "PHARMACIST_SUPPORT"
  | "RETAIL"
  | "DRIVER"
  | "PHARMACIST_CONSULTANT"
  | "UNSUPPORTED";

export const resolveSessionPersona = (
  roles: string[] | null | undefined,
): SessionPersonaKind => {
  const list = roles ?? [];
  if (list.includes("ADMIN")) return "ADMIN";
  if (list.includes("MARKETPLACE_ADMIN_USER")) return "MARKETPLACE_ADMIN";
  if (list.includes("PHARMACIST_SUPPORT_USER")) return "PHARMACIST_SUPPORT";
  if (list.includes("PRODAJNO_MESTO_USER")) return "RETAIL";
  if (derivePharmacistPersona(list).isConsultOnly) {
    return "PHARMACIST_CONSULTANT";
  }
  if (list.includes("DELIVERY_USER")) return "DRIVER";
  // Includes MEDIC_SPECIALIST_USER + PHARMACIST_USER (decision 6a): no branch
  // matches, so the session is refused on every path.
  return "UNSUPPORTED";
};

/**
 * ITAM-2622 (3.2/3.6): the identity a persona-changing refresh is measured
 * against. It needs BOTH axes, because each one is blind to what the other
 * sees:
 *
 *  - `resolveSessionPersona` separates the staff branches (ADMIN vs
 *    MARKETPLACE_ADMIN vs DRIVER ...), but it answers `RETAIL` for a retail-only
 *    AND for a dual-role session, since `PRODAJNO_MESTO_USER` matches first by
 *    design. On its own it therefore cannot see pharmacist capability being
 *    granted or revoked underneath an unchanged retail branch — and that is
 *    exactly the transition 3.6 exists for: the consult surface unmounts (the
 *    manager key does change) while `armedConsult`, the pharmacy context and the
 *    other consultation slices would survive without a reset.
 *  - `derivePersonaKey` is the pharmacist-capability axis (3.2 names this one:
 *    "a refresh whose `derivePharmacistPersona` output changed persona"), but it
 *    collapses every non-pharmacist staff role into `other`.
 *
 * Concatenating them keeps both discriminations. Over-reporting a change is
 * safe here — a reset costs a refetch — while under-reporting one is the leak.
 */
export const resolvePersonaIdentity = (
  roles: string[] | null | undefined,
): string => `${resolveSessionPersona(roles)}:${derivePersonaKey(roles)}`;

export const login = (
  credentials: { email: string | null; password: string | null },
  refreshLogin = false,
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    dispatch(authActions.loginStart());

    //Disable for deploy
    // const url = 'http://localhost:8081/auth/oauth/token'

    //Enable for deploy
    // const url = 'https://apoteka.rs:8443/authorization_server/oauth/token';

    //Enable for deploy for dev server
    const url = process.env.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL + "/authorization_server/oauth/token"
      : "https://test.apoteka.rs:8443/authorization_server/oauth/token";

    const params = new URLSearchParams();
    if (refreshLogin) {
      params.append("grant_type", "refresh_token");
      params.append(
        "refresh_token",
        localStorage.getItem("refreshToken") || "",
      );
    } else {
      params.append("grant_type", "password");
      params.append("username", credentials.email ? credentials.email : "null");
      params.append(
        "password",
        credentials.password ? credentials.password : "null",
      );
    }

    try {
      const response = await axios.post(url, params, {
        auth: {
          username: "apoteka_rs",
          password: "F7Q8T97@a'QveBX?",
        },
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      let approveLogin = false;
      let skipMarketplaceFetch = false;
      let isAdminUser = false; // Track if user is admin

      // ITAM-2622 (3.2/3.6): a token refresh dispatches `loginSuccess`, never
      // `auth/logout`, so a refresh that changes persona would otherwise carry
      // the previous persona's state — including consult patient PII — into the
      // new one. Read the OLD roles before the localStorage write below.
      if (refreshLogin) {
        const previousToken = localStorage.getItem("token");
        const previousPersona = previousToken
          ? resolvePersonaIdentity(getTokenAuthorities(previousToken))
          : "UNSUPPORTED:other";
        const nextPersona = resolvePersonaIdentity(
          response.data.userAuthorities,
        );
        if (previousPersona !== nextPersona) {
          console.log(
            `Auth: persona changed on refresh (${previousPersona} -> ${nextPersona}), resetting session state`,
          );
          dispatch(authActions.personaExit());
        }
      }

      // The precedence lives in `resolveSessionPersona` alone — this switch
      // only attaches each persona's side effects. Keeping the classification
      // out of here is what fixed the partner login loop: the duplicated
      // if/else chains had drifted a DELIVERY_USER check ahead of the
      // pharmacist branch.
      switch (resolveSessionPersona(response.data.userAuthorities)) {
        case "ADMIN":
          dispatch(authActions.setIsAdmin());
          approveLogin = true;
          isAdminUser = true; // Mark as admin user
          skipMarketplaceFetch = true;
          break;
        case "MARKETPLACE_ADMIN":
          dispatch(authActions.setIsMarketplaceAdmin());
          approveLogin = true;
          skipMarketplaceFetch = true;
          break;
        case "PHARMACIST_SUPPORT":
          dispatch(authActions.setIsPharmacist());
          approveLogin = true;
          break;
        case "RETAIL":
          dispatch(authActions.setRegularUser());
          approveLogin = true;
          break;
        case "PHARMACIST_CONSULTANT":
          // ITAM-2622: `skipMarketplaceFetch` because GET /marketplace/user/{id}
          // is ADMIN|PRODAJNO_MESTO_USER-only: without it, its 403 trips the
          // axios interceptor and logs the pharmacist out during login.
          dispatch(authActions.setIsPharmacistConsultant());
          approveLogin = true;
          skipMarketplaceFetch = true;
          break;
        case "DRIVER":
          dispatch(authActions.setIsDriver());
          approveLogin = true;
          break;
        default:
          if (refreshLogin) {
            // A refresh that dropped the last supported role. The localStorage
            // token writes below live inside `if (approveLogin)`, so nothing is
            // rewritten here — the OLD token would stay valid in both Redux and
            // localStorage and the user would remain authenticated on a role
            // set the app no longer supports. Discard it.
            console.error(
              "Auth: token refresh returned an unsupported role set, clearing the session",
            );
            dispatch(authActions.personaExit());
            dispatch(authActions.logout());
            dispatch(authActions.setAuthInitialized(true));
            return;
          }
          // Fresh attempt: keep `loginFailed()`. `authActions.logout()` sets
          // loginError = false, which would erase the very error the auth page
          // needs to show — and there is no session to clear on a fresh attempt.
          dispatch(authActions.loginFailed());
      }

      // dozvoli login samo ako ima neku od navedenih rola
      if (approveLogin) {
        const expirationDate = new Date(
          new Date().getTime() + response.data.expires_in * 1000,
        );

        // Clear marketplace info for non-admin users only
        if (!refreshLogin && !isAdminUser) {
          localStorage.removeItem("marketplaceId");
          localStorage.removeItem("branchMarketplaceId");
        }

        localStorage.setItem("token", response.data.access_token);
        localStorage.setItem("refreshToken", response.data.refresh_token);
        localStorage.setItem("expirationDate", expirationDate.toString());
        localStorage.setItem("id", response.data.userDetails.id);
        localStorage.setItem("email", response.data.userDetails.username);
        localStorage.setItem("firstName", response.data.userDetails.firstName);
        localStorage.setItem(
          "secondName",
          response.data.userDetails.secondName,
        );
        localStorage.setItem("roles", response.data.userAuthorities);

        dispatch(
          authActions.userInfoFn({
            id: response.data.userDetails.id,
            username: response.data.userDetails.username,
            firstName: response.data.userDetails.firstName,
            secondName: response.data.userDetails.secondName,
            roles: response.data.userAuthorities,
          }),
        );

        dispatch(authActions.setIsInitialLogin(!refreshLogin));
        dispatch(authActions.loginSuccess(response.data.access_token));

        // Fetch marketplace info for non-admin users during both fresh login and token refresh
        if (!skipMarketplaceFetch) {
          console.log(
            `Auth: Fetching marketplace info for non-admin user (${
              refreshLogin ? "token refresh" : "fresh login"
            })`,
          );
          await dispatch(
            fetchMarketplaceInfoOnLogin(response.data.userDetails.id),
          );
        } else {
          console.log("Auth: Skipping marketplace info fetch for admin user");
        }

        dispatch(checkLoginTimeout(response.data.expires_in));
        dispatch(authActions.setAuthInitialized(true));
      }
    } catch (error: any) {
      console.error("Auth: Login error:", error);

      if (refreshLogin) {
        // Refresh token failed - clear all auth state and redirect to login
        console.error(
          "Auth: Refresh token failed, clearing auth state and logging out",
          error,
        );
        dispatch(authActions.logout());
        dispatch(authActions.setAuthInitialized(true));
        return;
      }

      // Regular login error handling
      if (error.response?.data?.error_description === "Bad credentials") {
        console.error("Auth: Bad credentials provided");
        dispatch(authActions.loginFailed());
      } else {
        console.error("Auth: Login failed with server error");
        dispatch(authActions.loginFailed());
      }

      dispatch(authActions.setAuthInitialized(true));
    }
  };
};

export const fetchMarketplaceInfo = (
  user_id: number,
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    console.log(`Auth: Fetching marketplace info for user ${user_id}`);
    dispatch(authActions.fetchMarketplaceInfoStart());

    try {
      const response = await axios.get(
        baseUrl + "/marketplace/user/" + user_id,
        {
          params: { access_token: localStorage.getItem("token") },
        },
      );

      console.log("Auth: Marketplace info fetched successfully", response.data);
      dispatch(authActions.marketplaceInfo(response.data));
      localStorage.setItem("marketplaceId", response.data.marketplaceId);
      localStorage.setItem(
        "branchMarketplaceId",
        response.data.branchMarketplaceId,
      );
    } catch (error: any) {
      console.error("Auth: Failed to fetch marketplace info", error);

      // Handle authentication errors specifically
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.error(
          "Auth: Marketplace info fetch failed due to authentication error, token is invalid",
        );
        // The global interceptor will handle the logout, but let's also dispatch it here for safety
        dispatch(authActions.logout());
      } else {
        console.error(
          "Auth: Marketplace info fetch failed with non-auth error:",
          error.response?.status,
        );
      }

      dispatch(authActions.fetchMarketplaceInfoFailed(error));
    }
  };
};

export const fetchMarketplaceInfoOnLogin = (
  user_id: number,
): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    dispatch(authActions.fetchMarketplaceInfoStart());

    try {
      const response = await axios.get(
        baseUrl + "/marketplace/user/" + user_id,
        {
          params: { access_token: localStorage.getItem("token") },
        },
      );

      // Validate response data before using
      if (!response.data.branchMarketplaceId || !response.data.marketplaceId) {
        throw new Error("Invalid marketplace info received from server");
      }

      // Set localStorage AND Redux state atomically
      localStorage.setItem("marketplaceId", response.data.marketplaceId);
      localStorage.setItem(
        "branchMarketplaceId",
        response.data.branchMarketplaceId,
      );

      dispatch(authActions.marketplaceInfo(response.data));

      // Log successful marketplace info fetch
      console.log("Auth: Marketplace info fetched successfully", {
        branchMarketplaceId: response.data.branchMarketplaceId,
        marketplaceId: response.data.marketplaceId,
      });

      // NEW: Also fetch detailed marketplace info for license data
      await dispatch(
        fetchMarketplaceInfoDetailed(
          response.data.branchMarketplaceId,
          response.data.marketplaceId,
        ),
      );

      // Set active status after marketplace info is confirmed
      dispatch(
        setCNCActiveStatus(
          response.data.branchMarketplaceId,
          response.data.marketplaceId,
          1,
        ),
      );
    } catch (error: any) {
      console.error(
        "Auth: Failed to fetch marketplace info for non-admin user",
        error,
      );
      dispatch(authActions.fetchMarketplaceInfoFailed(error));
    }
  };
};

export const logoutAction = (
  branchMarketplaceId: number | null,
  marketplaceId: number | null,
  activeStatus: number,
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    const URL =
      "/marketplace/takes_orders/" +
      branchMarketplaceId +
      "/" +
      marketplaceId +
      "?takesOrders=" +
      activeStatus;
    axios
      .put(URL)
      .then((response) => {
        dispatch(authActions.setActiveStatus(activeStatus));
        dispatch(authActions.logout());
      })
      .catch((error) => {
        console.log("Error se desio" + error);
        // dispatch(orderActions.fetchNewOrderFailed())
      });
  };
};

export const setCNCActiveStatus = (
  branchMarketplaceId: number,
  marketplaceId: number,
  activeStatus: number,
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    const URL =
      "/marketplace/takes_orders/" +
      branchMarketplaceId +
      "/" +
      marketplaceId +
      "?takesOrders=" +
      activeStatus;
    axios
      .put(URL)
      .then((response) => {
        dispatch(authActions.setActiveStatus(activeStatus));
      })
      .catch((error) => {
        console.log("Error se desio" + error);
        // dispatch(orderActions.fetchNewOrderFailed())
      });
  };
};

export const pingActiveStatus = (
  branchMarketplaceId: number,
  marketplaceId: number,
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    // Use takes_orders endpoint to prevent clearing active pharmacy license
    const URL = `/marketplace/takes_orders/${branchMarketplaceId}/${marketplaceId}?takesOrders=1`;
    axios
      .put(URL)
      .then(() => {})
      .catch((error) => {
        console.log("Keep-alive ping error: " + error);
      });
  };
};

export const fetchMarketplaceInfoDetailed = (
  branchMarketplaceId: number,
  marketplaceId: number,
): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    dispatch(authActions.fetchMarketplaceInfoDetailedStart());

    try {
      const response = await axios.get(
        `${baseUrl}/marketplace/${marketplaceId}/${branchMarketplaceId}`,
      );

      dispatch(authActions.setMarketplaceInfoDetailed(response.data));

      console.log(
        "Auth: Detailed marketplace info with license fetched successfully",
      );
    } catch (error: any) {
      console.error("Auth: Failed to fetch detailed marketplace info", error);
      dispatch(authActions.fetchMarketplaceInfoDetailedFailed());
    }
  };
};

export const checkLoginTimeout = (
  time: number,
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return (dispatch) => {
    setTimeout(() => {
      if (
        localStorage.getItem("refreshToken") === null ||
        localStorage.getItem("refreshToken") === ""
      ) {
        const branchMarketplaceId = localStorage.getItem("branchMarketplaceId");
        const marketplaceId = localStorage.getItem("marketplaceId");
        // ITAM-2622: a consult-only pharmacist has no retail pair, and
        // PUT /marketplace/takes_orders/null/null is not a request worth
        // issuing. Log out directly instead.
        if (
          branchMarketplaceId &&
          marketplaceId &&
          branchMarketplaceId !== "null" &&
          marketplaceId !== "null"
        ) {
          dispatch(logoutAction(branchMarketplaceId as any, marketplaceId as any, 0));
        }
        dispatch(authActions.logout());
      } else {
        dispatch(login({ email: null, password: null }, true));
      }
    }, time * 1000);
  };
};

export const loginCheckState = (): ThunkAction<
  Promise<void>,
  RootState,
  unknown,
  AnyAction
> => {
  return async (dispatch) => {
    console.log("Auth: Initializing authentication state check");

    const token = localStorage.getItem("token");

    if (!token) {
      console.log("Auth: No token found, logging out");
      dispatch(authActions.logout());
      dispatch(authActions.setAuthInitialized(true));
      return;
    }

    // Client-side token validation
    const tokenValidation = validateToken(token);

    if (!tokenValidation.hasValidFormat) {
      console.error("Auth: Invalid token format, logging out");
      dispatch(authActions.logout());
      dispatch(authActions.setAuthInitialized(true));
      return;
    }

    if (tokenValidation.isExpired) {
      console.log("Auth: Token is expired, attempting refresh");
      const refreshToken = localStorage.getItem("refreshToken");

      if (!refreshToken) {
        console.log("Auth: No refresh token available, logging out");
        dispatch(authActions.logout());
        dispatch(authActions.setAuthInitialized(true));
        return;
      }

      // Attempt refresh
      dispatch(login({ email: null, password: null }, true));
      // Note: authInitialized will be set in login() after completion
      return;
    }

    // ITAM-2622 (3.2): classify BEFORE committing. `loginSuccess(token)` below
    // is what authenticates the session, and `loginFailed()` would not undo it
    // — it sets only loadingLogin/loginError and never clears the token. A
    // token whose role set matches no branch (a medic, decision 6a, or any
    // other unsupported set) must therefore be rejected here, before the
    // commit, the background marketplace fetch, and `checkLoginTimeout`.
    const restoredRoles = getTokenAuthorities(token);
    if (resolveSessionPersona(restoredRoles) === "UNSUPPORTED") {
      console.error(
        "Auth: restored token carries an unsupported role set, clearing the session",
      );
      dispatch(authActions.personaExit());
      dispatch(authActions.logout());
      dispatch(authActions.setAuthInitialized(true));
      return;
    }

    // Token is valid, restore auth state
    console.log("Auth: Valid token found, restoring session");
    dispatch(authActions.setIsInitialLogin(false));
    dispatch(authActions.loginSuccess(token));

    if (localStorage.getItem("id")) {
      dispatch(
        authActions.userInfoFn({
          id: localStorage.getItem("id")!,
          username: localStorage.getItem("email")!,
          firstName: localStorage.getItem("firstName")!,
          secondName: localStorage.getItem("secondName")!,
          roles: localStorage.getItem("roles")!.split(","),
        }),
      );

      // Restore role flags and track admin status. Same single classification
      // as the login chain — only the side effects live here.
      let skipMarketplaceFetch = false;
      switch (resolveSessionPersona(restoredRoles)) {
        case "ADMIN":
          dispatch(authActions.setIsAdmin());
          skipMarketplaceFetch = true;
          break;
        case "MARKETPLACE_ADMIN":
          dispatch(authActions.setIsMarketplaceAdmin());
          skipMarketplaceFetch = true;
          break;
        case "PHARMACIST_SUPPORT":
          dispatch(authActions.setIsPharmacist());
          break;
        case "RETAIL":
          dispatch(authActions.setRegularUser());
          break;
        case "PHARMACIST_CONSULTANT":
          // ITAM-2622: same reason for the skip as the login chain (the
          // marketplace lookup is 403 for this role, and the interceptor
          // turns that into a logout).
          dispatch(authActions.setIsPharmacistConsultant());
          skipMarketplaceFetch = true;
          break;
        case "DRIVER":
          dispatch(authActions.setIsDriver());
          break;
        default:
          // UNSUPPORTED was already rejected before the commit above.
          break;
      }

      // BACKGROUND DATA LOADING: Fetch marketplace info without blocking UI
      // This restores the missing data initialization while keeping the app responsive
      const userId = localStorage.getItem("id");
      if (!skipMarketplaceFetch && userId) {
        const parsedId = parseInt(userId, 10);
        if (!isNaN(parsedId)) {
          console.log(
            "Auth: Starting background marketplace info fetch during session restoration",
          );

          // Background fetch without blocking auth initialization
          const backgroundFetch = async () => {
            try {
              await dispatch(fetchMarketplaceInfoOnLogin(parsedId));
              console.log(
                "Auth: Background marketplace info fetch completed successfully",
              );
            } catch (error: any) {
              console.error(
                "Auth: Background marketplace info fetch failed",
                error,
              );
              // Could implement retry logic or user notification here if needed
            }
          };
          backgroundFetch(); // Fire and forget
        }
      } else if (skipMarketplaceFetch) {
        console.log(
          "Auth: Skipping marketplace info fetch for admin user during session restoration",
        );
      }
    }

    // Set up timeout based on actual token expiration
    const expirationDate = new Date(
      localStorage.getItem("expirationDate") || "",
    );
    const timeUntilExpiry = Math.max(
      0,
      (expirationDate.getTime() - new Date().getTime()) / 1000,
    );

    dispatch(checkLoginTimeout(timeUntilExpiry));

    // OPTIMISTIC AUTH INITIALIZATION: Set auth initialized immediately
    // This eliminates the AuthLoadingSpinner delay by separating auth validation from app data fetching
    dispatch(authActions.setAuthInitialized(true));
  };
};
